// =============================================================================
// src/data/analytics-overlay-scope.ts — Escopo, periodo e frescor no patch vivo.
//
// 2026-09-02: KPIs, manchete, contagens do rail e serie do chart passam a somar
// SO o recorte ativo (Nucleo · Omar · Contexto, com/sem reacoes) a partir de
// sl_serving_v_mentions_daily_scoped (migration 025); o periodo (7 ou 30 dias)
// deixa de ser cosmetico; e o eyebrow "atualizado ha X" sai de
// sl_serving_v_ingestion_freshness em vez de um texto fixo. Funcoes puras.
// =============================================================================

import type { AnalyticsDashboardFetchOptions } from './analytics-client';
import type {
  AnalyticsIngestionFreshnessRow,
  AnalyticsMentionsDailyRow,
  AnalyticsMentionsDailyScopedRow,
} from './analytics-live-query';
import type { AnalyticsFreshness } from '../features/analytics/analytics-live-store';
import type { OmarAnalyticsPeriod } from '../features/analytics/analytics-types';

/** Coleta parada ha mais de 6h vira aviso na tela (o buraco de 20-29/08 ficou invisivel). */
export const ANALYTICS_FRESHNESS_STALE_MINUTES = 6 * 60;

const ANALYTICS_PERIOD_DAYS: Readonly<Record<OmarAnalyticsPeriod, number>> = { last7: 7, last30: 30 };
const ANALYTICS_PERIOD_LABELS: Readonly<Record<OmarAnalyticsPeriod, string>> = {
  last7: 'ultimos 7 dias',
  last30: 'ultimos 30 dias',
};

/** Dias da janela dos KPIs/tops para o periodo da UI. */
export function analyticsPeriodWindowDays(period: OmarAnalyticsPeriod): number {
  return ANALYTICS_PERIOD_DAYS[period];
}

/** Rotulo do periodo no lede/KPIs ("ultimos 30 dias"). */
export function analyticsPeriodLabelFor(period: OmarAnalyticsPeriod): string {
  return ANALYTICS_PERIOD_LABELS[period];
}

/** Linha da diaria por escopo pertence ao recorte ativo? */
export function analyticsRowMatchesScope(
  scopedRow: AnalyticsMentionsDailyScopedRow,
  view: AnalyticsDashboardFetchOptions,
): boolean {
  const substanceOk = scopedRow.is_substantivo || view.showReactions;
  if (!substanceOk) return false;
  if (view.scope === 'contexto') return !scopedRow.is_nucleo;
  if (view.scope === 'omar') return scopedRow.is_nucleo && scopedRow.is_vip;
  return scopedRow.is_nucleo;
}

/**
 * Linhas diarias do recorte ativo, no shape da v_mentions_daily — os builders
 * de KPI/serie/rail continuam os mesmos (DRY). Reacoes 'vazio' nunca entram
 * (a view marca is_substantivo=false para elas e o toggle so re-inclui
 * 'reacao' no feed; aqui a diaria nao distingue as duas — conservador).
 *
 * @example analyticsScopedDailyRows(rows, { scope: 'omar', period: 'last30', showReactions: false })
 */
export function analyticsScopedDailyRows(
  scopedRows: readonly AnalyticsMentionsDailyScopedRow[],
  view: AnalyticsDashboardFetchOptions,
): readonly AnalyticsMentionsDailyRow[] {
  return scopedRows
    .filter((scopedRow) => analyticsRowMatchesScope(scopedRow, view))
    .map(({ is_nucleo: _nucleo, is_vip: _vip, is_substantivo: _substantivo, ...dailyRow }) => dailyRow);
}

/** Subtitulo do KPI "Mencoes": explica ao operador o que o numero conta. */
export function analyticsScopeKpiSubtitle(view: AnalyticsDashboardFetchOptions): string {
  if (view.scope === 'omar') return view.showReactions ? 'so Omar, com reacoes' : 'so Omar, sem reacoes';
  if (view.scope === 'contexto') return 'contexto local, sem carga politica';
  return view.showReactions ? 'nucleo do cenario AM, com reacoes' : 'nucleo do cenario AM, sem reacoes';
}

/** Frescor da ingestao: idade da ultima coleta (em relacao ao relogio do fetch). */
export function buildAnalyticsFreshnessPatch(
  freshnessRows: readonly AnalyticsIngestionFreshnessRow[],
  fetchedAtMs: number,
): AnalyticsFreshness | undefined {
  const latestIso = analyticsLatestCollectedAt(freshnessRows);
  if (!latestIso) return undefined;
  const ageMinutes = Math.max(0, Math.round((fetchedAtMs - Date.parse(latestIso)) / 60000));
  return {
    lastCollectedAtIso: latestIso,
    ageMinutes,
    isStale: ageMinutes > ANALYTICS_FRESHNESS_STALE_MINUTES,
    label: analyticsAgeLabel(ageMinutes),
    platformsCollecting24h: freshnessRows.filter((freshnessRow) => freshnessRow.collected_24h > 0).length,
  };
}

function analyticsLatestCollectedAt(freshnessRows: readonly AnalyticsIngestionFreshnessRow[]): string | null {
  let latestIso: string | null = null;
  for (const freshnessRow of freshnessRows) {
    const candidate = freshnessRow.last_collected_at;
    if (candidate && (latestIso === null || Date.parse(candidate) > Date.parse(latestIso))) latestIso = candidate;
  }
  return latestIso;
}

// 2026-09-20: a mesma fronteira de dia dos rollups (as views agregam com
// AT TIME ZONE 'America/Manaus'). Formatar o rodape em UTC faria a data pular de
// dia toda noite depois das 20h local, sem erro nenhum.
const ANALYTICS_COLLECTED_AT_TIME_ZONE = 'America/Manaus';

const ANALYTICS_COLLECTED_AT_DAY_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit', month: 'short', year: 'numeric', timeZone: ANALYTICS_COLLECTED_AT_TIME_ZONE,
});
// hourCycle h23 e deliberado: com h24 a meia-noite sai "24h00" do dia anterior.
const ANALYTICS_COLLECTED_AT_TIME_FORMAT = new Intl.DateTimeFormat('pt-BR', {
  hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: ANALYTICS_COLLECTED_AT_TIME_ZONE,
});

/**
 * Instante da ultima coleta no formato dos rodapes, COM HORA.
 *
 * O dia sozinho nao serve: o limiar de "coleta parada" do projeto e de 6h
 * (ANALYTICS_FRESHNESS_STALE_MINUTES) e uma data sem hora esconderia ate ~24h de
 * parada — que e exatamente o bug que este rodape veio consertar.
 *
 * @example analyticsCollectedAtLabel('2026-09-20T03:00:00Z') // '19 set 2026, 23h00'
 */
export function analyticsCollectedAtLabel(iso: string): string {
  const collectedAt = new Date(iso); // construido a partir do DADO, nunca do relogio
  const day = ANALYTICS_COLLECTED_AT_DAY_FORMAT.format(collectedAt).replace(/\./g, '').replace(/ de /g, ' ');
  return `${day}, ${ANALYTICS_COLLECTED_AT_TIME_FORMAT.format(collectedAt).replace(':', 'h')}`;
}

/**
 * Rotulo "atualizado ..." dos rodapes com dado vivo, ou null quando nao ha
 * frescor. null e resposta, nao ausencia: sem coleta conhecida o rodape NAO
 * inventa data — foi o literal congelado que envelheceu sozinho ate 09/06/2026.
 *
 * @example analyticsProvenanceUpdateLabel(getAnalyticsFreshness())
 */
export function analyticsProvenanceUpdateLabel(freshness: AnalyticsFreshness | null): string | null {
  if (!freshness) return null;
  return analyticsCollectedAtLabel(freshness.lastCollectedAtIso);
}

/** "ha 12 min" · "ha 3 h" · "ha 2 dias" — idade legivel no eyebrow. */
export function analyticsAgeLabel(ageMinutes: number): string {
  if (ageMinutes < 60) return `ha ${ageMinutes} min`;
  if (ageMinutes < 48 * 60) return `ha ${Math.floor(ageMinutes / 60)} h`;
  return `ha ${Math.floor(ageMinutes / (24 * 60))} dias`;
}
